import { useState, useEffect, useRef } from 'react'
import { apiClient, useAuthStore } from '@indipia/shared'
import { Button, Page, PageHeader, Switch } from '@indipia/ui-core'
import type { NavFn } from '../pages/SchedulerPage'
import { ErrorBanner } from '../components/AsyncState'
import { TodayTodoNotiSettingRow } from '../components/TodayTodoNotiSettingRow'

type Props = { navigate: NavFn }

export function SettingsView({ navigate: _navigate }: Props) {
  const user = useAuthStore(s => s.user)

  const [startNotiYn,    setStartNotiYn]   = useState(false)
  const [deliveryNotiYn, setDeliveryNotiYn]= useState(false)
  const [orderNotiYn,    setOrderNotiYn]   = useState(false)
  const [loading,  setLoading]             = useState(true)
  const [error,    setError]               = useState('')
  const [loadError, setLoadError]          = useState(false)
  const [reloadKey, setReloadKey]          = useState(0)

  const initializedRef = useRef(false)
  const saveTimerRef    = useRef<ReturnType<typeof setTimeout> | null>(null)

  const headers = {
    ...(user?.compId   ? { 'X-Comp-Id': String(user.compId)   } : {}),
    ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
  }

  useEffect(() => {
    setLoading(true)
    setLoadError(false)
    apiClient.get<{ data: {
      notiSetting?: { constStartNotiYn?: string; arrivalNotiYn?: string; orderNotiYn?: string }
    } }>('/schedule/settings', { headers })
      .then(res => {
        const noti = res.data?.data?.notiSetting ?? {}
        setStartNotiYn(noti.constStartNotiYn === 'Y')
        setDeliveryNotiYn(noti.arrivalNotiYn === 'Y')
        setOrderNotiYn(noti.orderNotiYn === 'Y')
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey])

  const handleSave = async () => {
    setError('')
    try {
      await apiClient.patch('/schedule/settings', {
        notiSetting: {
          constStartNotiYn: startNotiYn    ? 'Y' : 'N',
          arrivalNotiYn:    deliveryNotiYn ? 'Y' : 'N',
          orderNotiYn:      orderNotiYn    ? 'Y' : 'N',
        },
      }, { headers })
    } catch {
      setError('저장 중 오류가 발생했습니다.')
    }
  }

  const handleReset = () => {
    setStartNotiYn(false); setDeliveryNotiYn(false); setOrderNotiYn(false)
  }

  // 변경 시 자동 저장 (디바운스) — 최초 로드로 상태가 채워지는 시점은 저장에서 제외
  useEffect(() => {
    if (loading) return
    if (!initializedRef.current) { initializedRef.current = true; return }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => { void handleSave() }, 600)
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, startNotiYn, deliveryNotiYn, orderNotiYn])

  return (
    <div style={{ minHeight: '100%' }}>
      {loading ? (
        <div aria-busy="true" style={{ textAlign: 'center', padding: '60px 0', color: 'var(--shell-hint)', fontSize: 13 }}>불러오는 중...</div>
      ) : (
        <Page width="md">
          <PageHeader title="카카오 알림톡 설정" subtitle="카카오 알림톡 발송 여부를 설정하세요" />

          {loadError && (
            <div style={{ marginBottom: 16 }}>
              <ErrorBanner message="저장된 설정을 불러오지 못했습니다." onRetry={() => setReloadKey(k => k + 1)} />
            </div>
          )}

          {/* 알림 토글 카드 */}
          <div style={{ background: 'var(--shell-surface)', border: '1px solid var(--shell-border)', borderRadius: 16, overflow: 'hidden', marginBottom: 20 }}>
            {([
              { key: 'start',    on: startNotiYn,    set: setStartNotiYn,    label: '공사 시작일 알림', desc: '예) 착공일이 6월 20일이면 전날인 6월 19일과 당일 6월 20일, 두 번 알림을 발송합니다' },
              { key: 'delivery', on: deliveryNotiYn,  set: setDeliveryNotiYn, label: '입고일 알림',      desc: '예) 입고일이 6월 20일이면 전날인 6월 19일과 당일 6월 20일, 두 번 알림을 발송합니다' },
              { key: 'order',    on: orderNotiYn,     set: setOrderNotiYn,    label: '발주 알림',        desc: '예) 발주해야 할 날이 6월 20일이면 전날인 6월 19일에 알림을 발송합니다\n(발주해야 할 날짜는 설정 > 발주-입고 납기 설정에서 관리)' },
            ] as { key: string; on: boolean; set: (v: (prev: boolean) => boolean) => void; label: string; desc: string }[]).map(({ key, on, set, label, desc }, i) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px 18px', borderTop: i > 0 ? '1px solid var(--shell-border)' : 'none' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)' }}>{label}</div>
                  <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', marginTop: 2, whiteSpace: 'pre-line' }}>{desc}</div>
                </div>
                <Switch checked={on} onChange={() => set(v => !v)} />
              </div>
            ))}
            {/* 오늘 할일 알림 시각 — 자체 즉시 저장·초기화(OWNER 만 변경) */}
            <TodayTodoNotiSettingRow />
          </div>

          {/* 자동 저장 상태 */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
            {error && <div className="field error" style={{ marginRight: 'auto' }} role="alert"><span className="help">{error}</span></div>}
            <Button variant="primary" size="lg" onClick={handleReset}>초기화</Button>
          </div>

        </Page>
      )}
    </div>
  )
}
